import React from "react";
import { useTranslation } from "react-i18next";

import { List, ListItem, ListIcon, useColorModeValue, Box, Text } from "@chakra-ui/react";
import moment from "moment/moment";
import { IoMdCloseCircle } from "react-icons/io";
import { MdCheckCircle, MdSettings } from "react-icons/md";

import { fullDateFormat } from "../../utils/types";

export default function LeaseStatusTooltipLabel({ property, leaseStatus }) {
	const { t } = useTranslation();
	const listItemColor = useColorModeValue("gray.700", "gray.200");
	const dateColor = useColorModeValue("blue.600", "blue.300");
	const listSpacing = "2";

	const getListIcon = (status) => {
		switch (status) {
			case "warning":
				return <ListIcon as={MdSettings} color="orange.400" boxSize={5} />;
			case "complete":
				return <ListIcon as={MdCheckCircle} color="green.400" boxSize={5} />;
			case "incomplete":
				return <ListIcon as={IoMdCloseCircle} color="red.400" boxSize={5} />;
			case "expired":
				return <ListIcon as={IoMdCloseCircle} color="red.400" boxSize={5} />;
			default:
				return <ListIcon as={IoMdCloseCircle} color="red.400" boxSize={5} />;
		}
	};

	const getLeaseExpirationDate = () => {
		const lease = property.documents.find((doc) => doc.type === "lease" && doc.active);
		const extension = property.documents.find((doc) => doc.type === "extension" && doc.active);
		if (!lease) return "";
		if (extension) return `${moment(lease.startingDate).format(fullDateFormat)} - ${moment(extension.expirationDate).format(fullDateFormat)}`;
		return `${moment(lease.startingDate).format(fullDateFormat)} - ${moment(lease.expirationDate).format(fullDateFormat)}`;
	};

	return (
		<Box>
			<Text fontSize="sm" fontWeight="bold">
				{t("table.lease")}
			</Text>
			<List spacing={listSpacing}>
				{property.specs.apartmentFeatures.type === "garage" ? (
					<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
						{getListIcon(leaseStatus.lease)}
						{t("types.lease")}
					</ListItem>
				) : (
					<>
						<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
							{getListIcon(leaseStatus.lease)}
							<span>
								{t("types.lease")}: <span style={{ color: "var(--chakra-colors-" + dateColor.replace(".", "-") + ")" }}>{getLeaseExpirationDate()}</span>
							</span>
						</ListItem>
						<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
							{getListIcon(leaseStatus.entryReport)}
							{t("types.entryReport")}
						</ListItem>
						{leaseStatus.exitReport && (
							<>
								<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
									{getListIcon(leaseStatus.exitReport)}
									{t("types.exitReport")}
								</ListItem>
								<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
									{getListIcon(leaseStatus.termination)}
									{t("types.termination")}
								</ListItem>
							</>
						)}
						<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
							{getListIcon(leaseStatus.sanitationDeclaration)}
							{t("types.sanitationDeclaration")}
						</ListItem>
						<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
							{getListIcon(leaseStatus.tenantAssociationAnnouncement)}
							{t("types.tenantAssociationAnnouncement")}
						</ListItem>
						<ListItem display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
							{getListIcon(leaseStatus.tenants)}
							{t("types.tenants")}
						</ListItem>
						{property.tenants
							.filter((t) => !t.former)
							.map((tenant, index) => (
								<ListItem key={index} display="flex" alignItems="center" color={listItemColor} fontSize="sm" fontWeight="medium">
									{getListIcon(leaseStatus[tenant.email])}
									{tenant.name} - {t("types.jobCertificate")}
								</ListItem>
							))}
					</>
				)}
			</List>
		</Box>
	);
}
